import { create } from "zustand";
import { persist } from "zustand/middleware";
import {
  DEFAULT_BRANDING,
  composeBranding,
  isDarkBranding,
  type BrandingConfig,
  type ColorMode,
} from "@/lib/branding";

interface UIState {
  /** The ACTIVE palette, written to <html> as --brand-* by ThemeProvider. Never set directly:
   *  it is always `composeBranding(tenantPalette, mode)`, so it can only change when one of its
   *  two inputs does. Kept (and persisted) under this exact key because index.html's pre-paint
   *  script reads `state.branding` to paint before React mounts. */
  branding: BrandingConfig;
  /** This USER's own choice. A tenant's branding never takes it away. */
  mode: ColorMode;
  /** The WORKSPACE's brand palette, from the server; null means ORAI's own. Persisted so the
   *  pre-paint on the next load already shows the tenant's colours, not a flash of ORAI's. */
  tenantPalette: BrandingConfig | null;
  sidebarCollapsed: boolean;
  setMode: (mode: ColorMode) => void;
  setTenantPalette: (palette: BrandingConfig | null) => void;
  toggleSidebar: () => void;
}

// Persisted under "orai-ui-store" -- index.html's pre-paint script reads this exact
// key/shape to paint the palette before React ever mounts.
export const useUIStore = create<UIState>()(
  persist(
    (set, get) => ({
      branding: DEFAULT_BRANDING,
      mode: "light",
      tenantPalette: null,
      sidebarCollapsed: false,
      setMode: (mode) => set({ mode, branding: composeBranding(get().tenantPalette, mode) }),
      setTenantPalette: (tenantPalette) =>
        set({ tenantPalette, branding: composeBranding(tenantPalette, get().mode) }),
      toggleSidebar: () => set({ sidebarCollapsed: !get().sidebarCollapsed }),
    }),
    {
      name: "orai-ui-store",
      // v3: the palette stopped being each user's own. It is now the workspace's (set on the
      // Branding page) plus the user's light/dark choice. A v2 store held a palette the user
      // picked for themselves; what it still tells us is whether they wanted it DARK, so that
      // is the one thing carried forward -- nobody who had dark mode loses it.
      version: 3,
      migrate: (persisted, version) => {
        const saved = (persisted ?? {}) as Partial<UIState> & { branding?: BrandingConfig };
        if (version < 3) {
          const wasDark = saved.branding
            ? isDarkBranding({ ...DEFAULT_BRANDING, ...saved.branding })
            : false;
          const mode: ColorMode = wasDark ? "dark" : "light";
          return {
            ...saved,
            mode,
            tenantPalette: null,
            branding: composeBranding(null, mode),
          } as UIState;
        }
        return saved as UIState;
      },
      // A persisted palette saved before a key existed would arrive missing it, so the active
      // palette is recomputed from its inputs rather than trusted as stored.
      merge: (persisted, current) => {
        const saved = (persisted ?? {}) as Partial<UIState>;
        const mode: ColorMode = saved.mode === "dark" ? "dark" : "light";
        const tenantPalette = saved.tenantPalette
          ? { ...DEFAULT_BRANDING, ...saved.tenantPalette }
          : null;
        return {
          ...current,
          ...saved,
          mode,
          tenantPalette,
          branding: composeBranding(tenantPalette, mode),
        };
      },
    },
  ),
);
